import { disclaimer, feedbackVersionNumber, initialMessages, negativeFeedbackTemplate } from '../../config/constants'
import { useEffect, useRef, useState } from 'react'
import ChatBotContent from './components/chatBotContent'
import ChatBotFooter from './components/chatBotFooter'
import ChatBotHeader from './components/chatBotHeader'
import { Message } from '../../types/chatbot'
import HighlightExplain from './components/highlightExplain'
import styles from './styles/chatbot.module.scss'
import { getRestURL, getWebSocketURL } from '../../utils/getUrls.ts'

import {
  ClientProvider,
  EMPTY_CLIENT,
  useClient
} from '../../client/ClientProvider.tsx'
import { initDataType } from '../../types/initDataTypes.ts'
import { composeQueryParameter } from '../../utils/paramHelper.ts'

const ChatBotWidget = (initData: { [key: string]: string }) => {
  const [expanded, setExpanded] = useState(false)
  const [text, setText] = useState<string>('')
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [userInput, serUserInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)

  const client = useClient()

  const handleInputChange = (userInput: string) => {
    serUserInput(userInput)
  }

  useEffect(() => {
    if (client === EMPTY_CLIENT) {
      return
    }
    async function chatbot() {
      if (initData.gandalfToken) {
        await client.getCookie()
      }
      const queryParameter = composeQueryParameter(initData)
      let prepend = ''
      if (queryParameter.length > 0) {
        prepend = `?${queryParameter}`
      }
      wsRef.current = new WebSocket(
        `${getWebSocketURL(window.location.hostname)}${prepend}`
      )
      // open connection
      wsRef.current.onopen = () => {
        console.log(`WebSocket connection opened`)
      }

      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data)
        console.log(`WebSocket message received: ${event.data}`) // Let's keep this here for now while developing
        if (data.message !== '') {
          const botMessage: Message = {
            text: data.message,
            sender: 'bot',
            type: 'response'
          }
          setMessages((prevMessage) => [...prevMessage, botMessage])
          setIsLoading(false)
        }
      }

      wsRef.current.onclose = () => {
        console.log('WebSocket connect closed!')
      }

      wsRef.current.onerror = (error) => {
        console.error('WebSocket connect error', error)
        setIsLoading(false) // Stop loading when error occurs
      }
    }
    chatbot()

    return () => {
      wsRef.current?.close()
    }
  }, [client, initData])

  const sendMessage = (userMessage: Message) => {
    setMessages((prevMessage) => [...prevMessage, userMessage])
    serUserInput('')
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({
        action: 'sendMessage',
        message: userMessage.text,
        response_format: 'html',
        ...(initData.blueprintArn && { blueprintArn: initData.blueprintArn })
      })
      wsRef.current.send(message)
      setIsLoading(true)
    } else {
      console.error(`Websocket is not open!`)
    }
  }

  const handleSendMessage = () => {
    if (userInput.trim() !== '') {
      const userMessage: Message = { text: userInput, sender: 'user' }
      sendMessage(userMessage)
    }
  }

  const handleOptionButtonClick = (option: string) => {
    if (negativeFeedbackTemplate[feedbackVersionNumber].options.indexOf(option.trim()) >= 0) {
      const botMessage: Message = { text: negativeFeedbackTemplate[feedbackVersionNumber].response , sender: 'bot'}
      sendMessage(botMessage)
    } else if (option.trim() !== '') {
      const userMessage: Message = { text: option, sender: 'user' }
      sendMessage(userMessage)
    }
  }

  /**
   * This is used to communicate from hosting platform to send message to chatbot
   */
  useEffect(() => {
    if (text.trim() !== '') {
      if (expanded === false) setExpanded(true)
      const userMessage: Message = {
        text: `I have a question about ${text}`,
        sender: 'user'
      }
      sendMessage(userMessage)
    }
  }, [text])

  useEffect(() => {
    console.log(initData) // This is used when we integrate with other platforms
    const receiveMessage = (event: MessageEvent) => {
      if (event.data.type === 'ADD_TEXT') {
        setText(event.data.payload)
      } else if (event.data.type === 'OPEN_WIDGET') {
        setExpanded(true)
      }
    }

    window.addEventListener('message', receiveMessage)
    return () => window.removeEventListener('message', receiveMessage)
  }, [])

  const openChatBot = () => setExpanded(true)
  const closeChatBot = () => setExpanded(false)

  const handleFeedbackButtonClick = () => {
    setMessages((prevMessage) => [...prevMessage, {
      text: negativeFeedbackTemplate[feedbackVersionNumber].text,
      sender: 'bot',
      type: 'options',
      options: negativeFeedbackTemplate[feedbackVersionNumber].options
    }])
  }

  return (
    <div
      className={`${styles.chatbot} ${styles['chatbot-button']} ${
        expanded ? styles.expanded : styles['chatbot-button']
      }`}
    >
      <ChatBotHeader
        expanded={expanded}
        openChatBot={openChatBot}
        closeChatBot={closeChatBot}
      />

      {expanded && (
        <>
          <ChatBotContent
            disclaimer={disclaimer}
            messages={messages}
            handleOptionButtonClick={handleOptionButtonClick}
            handleFeedbackButtonClick={handleFeedbackButtonClick}
            isLoading={isLoading}
          />
          <ChatBotFooter
            userInput={userInput}
            onInputChange={handleInputChange}
            onEnterPress={handleSendMessage}
          />
        </>
      )}
      <HighlightExplain onExplain={setText} />
    </div>
  )
}

const ChatBot = (initData: initDataType) => {
  if (!initData.gandalfToken) {
    console.warn('No token is passed from parent.')
  }
  return (
    <ClientProvider
      endpoint={getRestURL(window.location.host)}
      initData={initData}
    >
      <ChatBotWidget {...initData} />
    </ClientProvider>
  )
}

export default ChatBot
